import type { GenerationRunInput } from '@clockwise/clients/invoices';
import {
  INVOICE_GENERATION_KIND_LABELS,
  INVOICE_GENERATION_KINDS,
  type InvoiceGenerationKind,
  isInvoiceGenerationKind,
  SOW_FREQUENCY_OPTIONS,
  type SowFrequency,
} from '@clockwise/shared';
import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, Loader2, Pencil, Upload } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { invoicesApi } from '@/lib/api';
import { isTerminalRunStatus, useRunPreview, useRunProgress } from '@/lib/invoice-run-hooks';

/**
 * The frequencies ticked when the dialog opens: all of them. The run decides
 * per engagement whether its cadence is due at this close — a quarterly
 * retainer bills only at a quarter-end, an annual one only at year-end — so
 * ticking them all never over-bills. One-Time is ticked too: it bills nothing
 * yet, but ticked its lines are reported as awaiting the billing schedule, so a
 * one-off fee nobody is raising stays visible unless the user sets it aside.
 */
const ALL_FREQUENCIES: readonly SowFrequency[] = SOW_FREQUENCY_OPTIONS.map((o) => o.value);

/**
 * `gFreqs` for a deliberately EMPTY selection. Absent means "the defaults", so
 * an empty selection needs a value of its own or the round trip through the
 * grid would re-tick everything. Matched explicitly in `initialFrequencySet`,
 * not left to fall out of `parseCsv` dropping an unknown value.
 */
const NO_FREQUENCIES = 'none';

/** The URL's frequencies: absent → every one; `none` → none. */
export function initialFrequencySet(raw: string | undefined): Set<SowFrequency> {
  if (raw === undefined || raw === '') return new Set(ALL_FREQUENCIES);
  if (raw === NO_FREQUENCIES) return new Set();
  return parseCsv(raw, SOW_FREQUENCY_OPTIONS);
}

/** The selection as `gFreqs`: omitted at the default, `none` when empty. */
export function frequencyParam(selected: ReadonlySet<SowFrequency>): string | undefined {
  if (selected.size === 0) return NO_FREQUENCIES;
  return isEveryFrequency(selected) ? undefined : [...selected].join(',');
}

/**
 * The selection as the run's `sowFrequencies`. Every frequency ticked is sent
 * as NO filter — what the API reads as every value — so a default run is
 * recorded as "All engagements" rather than a list that reads as narrowed.
 * Callers must not send an EMPTY selection at all (see `noFrequency`).
 */
export function frequencyFilter(
  selected: ReadonlySet<SowFrequency>,
): { sowFrequencies: SowFrequency[] } | Record<string, never> {
  return isEveryFrequency(selected) ? {} : { sowFrequencies: [...selected] };
}

function isEveryFrequency(selected: ReadonlySet<SowFrequency>): boolean {
  return ALL_FREQUENCIES.every((f) => selected.has(f));
}

/** The month a run would most likely be for: the one the most recent close
 *  produced. Computed from the browser's date only as a DEFAULT — the server
 *  derives the actual close date from the tenant's advance/arrears setting, so
 *  this never has to be right, only helpful. */
function defaultInvoiceMonth(today = new Date()): string {
  const y = today.getUTCFullYear();
  const m = today.getUTCMonth() + 1;
  return `${y}-${String(m).padStart(2, '0')}-01`;
}

/** `'2026-08-01'` → `'August 2026'`, for the month picker's own options. */
/** A close date as the user's calendar reads it. UTC-pinned like every other
 *  date in this dialog: these are calendar days, not instants. */
function dateLabel(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC' });
}

function monthLabel(iso: string): string {
  const [y, m] = iso.split('-');
  const name = new Date(Date.UTC(Number(y), Number(m) - 1, 1)).toLocaleString('en-US', {
    month: 'long',
    timeZone: 'UTC',
  });
  return `${name} ${y}`;
}

/** Twelve months back and three forward. Forward months are allowed — raising
 *  January early is legitimate — and the far past is where a missed close gets
 *  filled in. */
function monthOptions(today = new Date()): string[] {
  const out: string[] = [];
  for (let delta = 3; delta >= -12; delta--) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + delta, 1));
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`);
  }
  return out;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * A run was queued. The caller records it so the progress TOAST can take
   * over the moment this dialog closes — the dialog owns the in-flight surface
   * only while it is open.
   */
  onStarted: (runId: string) => void;
  /**
   * The run the PAGE is currently watching, if any.
   *
   * Lets this dialog adopt a run already in flight when it is reopened. Without
   * it, reopening mid-run cleared the dialog's own tracking AND unmounted the
   * toast (which is gated on the dialog being closed), so a live run had no
   * indicator anywhere and the Generate button came back enabled — offering a
   * click the server can only reject with "a generation run is already in
   * progress".
   */
  liveRunId: string | null;
  /**
   * Selections to restore, from the URL. What makes leaving for the grid and
   * coming back non-destructive: the links out carry the month and filters,
   * and the way back puts them here.
   */
  initialMonth?: string | undefined;
  initialFrequencies?: string | undefined;
  /** Which engine was selected before the user stepped out to the grid.
   *  Carried in the URL like the month and the filters, so coming back does
   *  not silently reseat them on Retainers. */
  initialKind?: string | undefined;
}

/**
 * Pick a month and (optionally) narrow by customer type or cadence, see what
 * would happen, then commit.
 *
 * The month is **the month being invoiced**, not the close date — in `advance`
 * mode those are not even the same month, and asking a finance user for a close
 * date leaks that inversion into a form. The server inverts it.
 */
export function GenerateInvoicesSheet({
  open,
  onOpenChange,
  onStarted,
  liveRunId,
  initialMonth,
  initialFrequencies,
  initialKind,
}: Props) {
  // Seeded from the URL here, not only in the on-open effect below: this
  // component can mount with `open` already true (the grid's way back links
  // straight to an open sheet), and an effect runs after the first paint. That
  // left the first render — and the preview request it fires — on the default
  // month with no filters, so arriving at a July URL briefly showed September's
  // 563 customers. The effect is still needed for RE-opening.
  const [invoiceMonth, setInvoiceMonth] = useState(() => initialMonth ?? defaultInvoiceMonth());
  /**
   * Which engine to run.
   *
   * Two runs, not one with a filter, because they bill on different clocks: a
   * retainer close raises a period that has not happened yet, a T&M run raises
   * one that has. Defaults to `retainer` — the month-end close is the routine
   * the finance team does every month, and T&M is the deliberate second pass a
   * week later.
   */
  const [kind, setKind] = useState<InvoiceGenerationKind>(() => parseKind(initialKind));
  // No engagement-type filter: a retainer run can only bill retainers, so the
  // one option it offered narrowed nothing.
  const [frequencies, setFrequencies] = useState<Set<SowFrequency>>(() =>
    initialFrequencySet(initialFrequencies),
  );
  /**
   * The run this dialog started and is still watching.
   *
   * Held here rather than only handed upward so the Generate button can report
   * real progress instead of the dialog vanishing the instant the run is
   * queued. It is NOT a second source of truth: the same run id goes to the
   * page via `onStarted`, and the toast picks it up as soon as this closes.
   */
  const [startedRunId, setStartedRunId] = useState<string | null>(null);

  const months = useMemo(() => monthOptions(), []);

  const input: GenerationRunInput = useMemo(
    () =>
      kind === 'tm'
        ? // Neither filter applies to a T&M run: it has one shape of work to
          // do, and a T&M line carries no frequency for a filter to match.
          // Sent anyway they would be silently ignored, which is worse than
          // not sending them.
          { invoiceMonth, kind }
        : { invoiceMonth, ...frequencyFilter(frequencies) },
    [invoiceMonth, kind, frequencies],
  );

  // An EMPTY frequency selection is "nothing to run", not "everything": the
  // API reads an absent filter as every value, so it is never sent — the
  // preview and Generate wait for a frequency instead.
  const noFrequency = kind === 'retainer' && frequencies.size === 0;
  const preview = useRunPreview(open && !noFrequency ? input : null);
  const start = useMutation({
    mutationFn: () =>
      // A fresh key per attempt, not per dialog: two deliberate runs for
      // different months must not de-duplicate into one.
      invoicesApi.startRun(input, { idempotencyKey: crypto.randomUUID() }),
    onSuccess: (res) => {
      if (res.data) {
        // Stay open and watch it. `onStarted` still fires immediately so the
        // page holds the id — closing this dialog then hands off to the toast
        // with nothing lost.
        setStartedRunId(res.data.runId);
        onStarted(res.data.runId);
        return;
      }
      onOpenChange(false);
      // `202 { data: null }` is "nothing to do", not an error — the eligible
      // set emptied between the preview and the click, usually because someone
      // else ran the same month first. Saying so matters: the dialog has just
      // closed, no invoices appeared and nothing else on the page moved, so
      // silence reads as a broken button (G15).
      toast.info('Nothing to generate', {
        description: 'Every eligible customer already has an invoice for this month.',
      });
    },
  });

  const runProgress = useRunProgress(startedRunId);
  const live = runProgress.data?.data;
  const finished = isTerminalRunStatus(live?.status);

  // Close on completion and let the toast say how it went. Deliberately not a
  // success state of its own: the completion message, the query invalidation
  // and the failure copy all live in `InvoiceRunProgress`, and a second place
  // that announces the same run is a second place to keep in step.
  useEffect(() => {
    if (finished) onOpenChange(false);
  }, [finished, onOpenChange]);

  // On open, ADOPT whatever run the page is watching — null when there is
  // none, which is the reset that stops a reopened dialog from reporting the
  // last run's progress. Seeding it rather than clearing is what keeps a
  // background run visible if the user comes back to look at it.
  //
  // `liveRunId` is deliberately not a dependency: this is a synchronisation on
  // OPENING, and re-running it when the page clears its id mid-session would
  // drop a run this dialog is legitimately still watching.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    setStartedRunId(liveRunId);
    // Seeded on OPEN rather than in a useState initializer: this component
    // stays mounted with `open=false`, so an initializer would only ever see
    // the params present at page load — never the ones a return trip adds.
    if (initialMonth) setInvoiceMonth(initialMonth);
    setFrequencies(initialFrequencySet(initialFrequencies));
    setKind(parseKind(initialKind));
  }, [open]);

  const inFlight = start.isPending || (startedRunId !== null && !finished);

  const eligible = preview.data?.data.eligible ?? 0;
  const canGenerate = !noFrequency && preview.isSuccess && eligible > 0 && !inFlight;

  const missing = preview.data?.data.missingVariableExpenses;
  const missingCount = missing?.count ?? 0;

  /**
   * Whether to warn at all. Conditional on purpose: most months nothing is
   * missing, and a warning shown every time is one nobody reads. Also gated on
   * `inFlight` — once a run is queued the warning is history, and must not push
   * the progress the user is waiting on off the screen.
   *
   * Rendered INLINE, not as a step in front of this screen. The warning is
   * computed from the month and the frequency filters, and those controls live
   * HERE — so a step before them asked the user to react to choices they had
   * not made yet, and then showed them this screen again to make them. It also
   * said the same thing twice, once in the step and once in the summary.
   */
  const showWarning = missingCount > 0;

  /** Everything the grid needs to send the user back here unchanged. */
  const gridSearch = useMemo(() => {
    const gFreqs = frequencyParam(frequencies);
    return {
      month: invoiceMonth,
      returnGenerate: true,
      // Carried even though it is the default: the round trip must restore
      // what the user chose, and pressing Generate raises real documents —
      // landing back on Retainers with a T&M month selected is how the wrong
      // engine gets run.
      gKind: kind,
      ...(gFreqs !== undefined ? { gFreqs } : {}),
    };
  }, [invoiceMonth, kind, frequencies]);

  /** The preview is what section 2 reports, so section 2 is busy whenever it
   *  is — including a REFETCH over an answer already on screen, which is the
   *  case that silently showed a stale count while the filters changed. */
  const checking = preview.isPending || preview.isFetching;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col sm:max-w-xl" side="right">
        <SheetHeader>
          <SheetTitle>Generate invoices</SheetTitle>
          <SheetDescription>
            Draft invoices are created for review. Nothing is approved or sent.
          </SheetDescription>
        </SheetHeader>

        {/* Both sections are ALWAYS rendered, and section two holds a minimum
            height. Revealing a section once the preview came back made the
            surface jump under the pointer, and the thing that moved was the
            button the user was reaching for. */}
        <div className="flex-1 space-y-6 overflow-auto px-4 py-2">
          <section className="space-y-4">
            <StepHeading n={1} title="What to bill" />

            <fieldset>
              <legend className="font-medium text-sm">What to run</legend>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                {INVOICE_GENERATION_KINDS.map((k) => (
                  <button
                    aria-pressed={kind === k}
                    className={
                      kind === k
                        ? 'rounded-md border border-primary bg-primary/5 px-3 py-2 text-left text-sm'
                        : 'rounded-md border border-input px-3 py-2 text-left text-sm hover:bg-accent'
                    }
                    key={k}
                    onClick={() => setKind(k)}
                    type="button"
                  >
                    <span className="block font-medium">{INVOICE_GENERATION_KIND_LABELS[k]}</span>
                    <span className="block text-muted-foreground text-xs">
                      {k === 'retainer'
                        ? 'Agreed fees, from the engagement'
                        : 'Approved hours, at the agreed rates'}
                    </span>
                  </button>
                ))}
              </div>
              {kind === 'tm' && (
                <p className="mt-1.5 text-muted-foreground text-xs">
                  Bills the month named below, in arrears. Only approved, billable hours are charged
                  — run this once the month’s timesheets have been approved.
                </p>
              )}
            </fieldset>

            <div>
              <Label htmlFor="invoice-month">Invoice month</Label>
              <select
                className="mt-1.5 h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                id="invoice-month"
                onChange={(e) => setInvoiceMonth(e.target.value)}
                value={invoiceMonth}
              >
                {months.map((m) => (
                  <option key={m} value={m}>
                    {monthLabel(m)}
                  </option>
                ))}
              </select>
              <p className="mt-1.5 text-muted-foreground text-xs">
                The service period customers will see on the invoice.
              </p>
            </div>

            {/* Hidden for a T&M run rather than disabled: a T&M line carries
                no frequency for the filter to narrow, and a greyed-out control
                invites the question of what it would have done. */}
            {kind === 'retainer' && (
              <div>
                {/* Every frequency starts ticked — see ALL_FREQUENCIES. Ticked,
                  One-Time reports its lines as awaiting the billing schedule;
                  unticked, they are set aside as out of scope. */}
                <CheckList
                  legend="Engagement frequency"
                  onToggle={(v) => setFrequencies(toggle(frequencies, v))}
                  options={SOW_FREQUENCY_OPTIONS}
                  selected={frequencies}
                />
                <p className="mt-1.5 text-muted-foreground text-xs">
                  Each engagement bills only when its frequency is due this month.
                </p>
              </div>
            )}

            {noFrequency ? (
              <div className="rounded-md bg-muted/40 p-3">
                <p className="text-muted-foreground text-sm">
                  Select at least one frequency to see what this would generate.
                </p>
              </div>
            ) : (
              <PreviewPanel
                canWiden={kind === 'retainer'}
                invoiceMonth={invoiceMonth}
                query={preview}
              />
            )}
          </section>

          <section className="space-y-3 border-t pt-5">
            <StepHeading n={2} title="Variable expenses" />

            <div className="min-h-20">
              {checking ? (
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Loader2 aria-hidden className="size-4 animate-spin" />
                  Checking which expenses have no amount…
                </div>
              ) : preview.isError ? (
                <p className="text-muted-foreground text-sm">
                  Couldn’t check variable expenses for this period.
                </p>
              ) : showWarning ? (
                <MissingExpensesNotice
                  gridSearch={gridSearch}
                  inFlight={inFlight}
                  invoiceMonth={invoiceMonth}
                  sample={missing?.sample ?? []}
                  total={missingCount}
                />
              ) : (
                <p className="text-muted-foreground text-sm">
                  Nothing to record — every variable expense in force for this period already has an
                  amount.
                </p>
              )}
            </div>
          </section>

          {start.isError && (
            <p className="text-destructive text-sm" role="alert">
              {start.error instanceof Error
                ? start.error.message
                : 'Could not start the run. Try again.'}
            </p>
          )}
        </div>

        {/* `SheetFooter` stacks its children (`flex-col`) where `DialogFooter`
            rows them — so moving to a sheet put Cancel above Generate, each
            full width. Overridden here rather than in the shared component, so
            every other sheet keeps its stacked default. */}
        <SheetFooter className="flex-row justify-end">
          {/* Dismissible on purpose, even mid-run. A 563-invoice run takes
              minutes and needs no supervision — the work is durable
              server-side and the toast keeps reporting it. A surface that
              trapped the user here would imply otherwise. */}
          <Button onClick={() => onOpenChange(false)} variant="outline">
            {inFlight ? 'Run in background' : 'Cancel'}
          </Button>
          <Button disabled={!canGenerate} onClick={() => start.mutate()}>
            {inFlight ? (
              <>
                <Loader2 aria-hidden className="mr-2 size-4 animate-spin" />
                {generateLabel(live?.done, live?.total)}
              </>
            ) : (
              'Generate'
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

/** A numbered step heading. The two sections are a sequence — pick what to
 *  bill, then see what is missing from it — and the numbers say so without
 *  making the second one a screen you have to click through. */
function StepHeading({ n, title }: { n: number; title: string }) {
  return (
    <div className="flex items-center gap-2.5">
      {/* Filled rather than a muted outline: at `bg-muted` these read as
          incidental labels beside the field captions they sit above, which is
          the opposite of what a step number is for. */}
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary font-semibold text-primary-foreground text-xs">
        {n}
      </span>
      <h3 className="font-semibold text-base tracking-tight">{title}</h3>
    </div>
  );
}

/** Comma-separated URL value → a validated Set. Unknown values are dropped
 *  rather than trusted: these arrive from a link anyone can edit. */
/**
 * The engine named in the URL, or `retainer`.
 *
 * Validated rather than cast: the value comes off a query string a user can
 * type, and an unrecognised one must land on the default rather than putting
 * an impossible kind into the run request.
 */
function parseKind(raw: string | undefined): InvoiceGenerationKind {
  return isInvoiceGenerationKind(raw) ? raw : 'retainer';
}

function parseCsv<T extends string>(
  raw: string | undefined,
  allowed: ReadonlyArray<{ value: T }>,
): Set<T> {
  if (!raw) return new Set<T>();
  const ok = new Set<string>(allowed.map((o) => o.value));
  return new Set(raw.split(',').filter((v): v is T => ok.has(v)));
}

/**
 * What the Generate button says while a run is in flight.
 *
 * "Starting…" until the first poll answers — NOT "0 of 0", which reads as a run
 * that found nothing to do. Once counts arrive it reports them, and the numbers
 * move because `/runs/:id/progress` counts the run's ITEMS rather than the run
 * row's cached totals (those are written only at finalize, which is what made
 * this counter appear frozen).
 */
function generateLabel(done: number | undefined, total: number | undefined): string {
  if (done === undefined || total === undefined || total === 0) return 'Starting…';
  return `Generating… ${done} of ${total}`;
}

function toggle<T>(set: ReadonlySet<T>, value: T): Set<T> {
  const next = new Set(set);
  if (!next.delete(value)) next.add(value);
  return next;
}

/** A labelled checkbox list. */
function CheckList<T extends string>({
  legend,
  options,
  selected,
  onToggle,
}: {
  legend: string;
  options: ReadonlyArray<{ value: T; label: string }>;
  selected: ReadonlySet<T>;
  onToggle: (value: T) => void;
}) {
  return (
    <fieldset>
      <legend className="font-medium text-sm">{legend}</legend>
      <div className="mt-1.5 max-h-40 overflow-auto rounded-md border border-input p-2">
        {options.map((o) => {
          // `htmlFor` rather than wrapping: the shadcn Checkbox renders a Radix
          // button, not an `<input>`, so a wrapping <label> associates with no
          // control — the text would not be clickable and a screen reader would
          // announce the checkbox unnamed.
          const id = `${legend}-${o.value}`;
          return (
            <div key={o.value} className="flex items-center gap-2 py-0.5">
              <Checkbox
                checked={selected.has(o.value)}
                id={id}
                onCheckedChange={() => onToggle(o.value)}
              />
              <Label className="font-normal text-sm" htmlFor={id}>
                {o.label}
              </Label>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}

/**
 * What the run would do.
 *
 * Two zero cases are spelled out rather than left as a bare "0 customers",
 * because both are legitimate selections and an unexplained zero reads as a
 * broken feature: a cadence that does not bill this month, and a month that is
 * already fully invoiced.
 */
function PreviewPanel({
  query,
  invoiceMonth,
  canWiden,
}: {
  query: ReturnType<typeof useRunPreview>;
  invoiceMonth: string;
  /** The frequency filter is on screen to widen — false for a T&M run. */
  canWiden: boolean;
}) {
  if (query.isError) {
    return (
      <div className="rounded-md border border-destructive/50 p-3" role="alert">
        <p className="text-destructive text-sm">
          {query.error instanceof Error
            ? query.error.message
            : "Couldn't work out what this would generate."}
        </p>
      </div>
    );
  }

  if (!query.data) {
    return (
      <div className="rounded-md bg-muted/40 p-3">
        <p className="text-muted-foreground text-sm">Working out what this would generate…</p>
      </div>
    );
  }

  const p = query.data.data;

  if (p.eligible === 0) {
    return (
      <div className="rounded-md bg-muted/40 p-3">
        <p className="font-medium text-sm">No engagements bill for {monthLabel(invoiceMonth)}.</p>
        <p className="mt-1 text-muted-foreground text-sm">
          {p.alreadyInvoiced > 0
            ? `${p.alreadyInvoiced} already have an invoice for this period.`
            : // Naming the close date is the whole point of this line. In
              // advance mode it sits in the PREVIOUS month, so "no SOWs bill
              // for August" while a SOW plainly starts in August is not a bug
              // — it is in force on 24 August and the close was 31 July. With
              // only "check the month", that reads as broken software.
              `Raised by the ${dateLabel(p.runDate)} close — no engagement is in force and due then. Check the month${canWiden ? ', or widen the frequencies above' : ''}.`}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md bg-muted/40 p-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-medium text-xl">{p.eligible}</span>
        <span className="text-muted-foreground text-sm">
          {p.eligible === 1 ? 'customer' : 'customers'}
          {p.invoiceDate !== null && ` · invoices dated ${dateLabel(p.invoiceDate)}`}
        </span>
      </div>
      {/* The period's OWN label, never the month picked: in arrears a
          multi-month period ENDS with the chosen month, so a quarterly
          customer asked for March is billed "Jan–Mar 2026". */}
      <p className="mt-1.5 text-muted-foreground text-sm">
        {p.periods
          .map(
            (period) =>
              // Dates only when they differ — one date is already said above.
              `${period.label} (${period.customerCount})${p.invoiceDate === null ? `, dated ${dateLabel(period.invoiceDate)}` : ''}`,
          )
          .join(' · ')}
      </p>
      {p.alreadyInvoiced > 0 && (
        <p className="mt-1 text-muted-foreground text-xs">
          {p.alreadyInvoiced} already invoiced for this period and will be skipped.
        </p>
      )}
    </div>
  );
}

/**
 * What this run will leave off, and the two ways to fix it.
 *
 * Leads with the CONSEQUENCE rather than the task. "12 expenses have no
 * amount" is a chore; "those lines will be left off the invoices" is the
 * reason anyone should care, and it is what the user is one click away from
 * causing.
 *
 * Nothing here blocks. `MISSING_VARIABLE_EXPENSE` is a `review` flag, never
 * `blocking`, and a step that gated Generate would contradict that — a month
 * where genuinely nothing was incurred is complete with every line empty. So
 * the primary action continues, and the escape hatches are secondary.
 */
function MissingExpensesNotice({
  total,
  sample,
  invoiceMonth,
  gridSearch,
  inFlight,
}: {
  total: number;
  sample: Array<{ customerName: string; description: string; itemName: string }>;
  invoiceMonth: string;
  /** Carries the month and filters so the grid can offer a way back here. */
  gridSearch: { month: string; returnGenerate: boolean; gFreqs?: string };
  /** A run is going. The warning still reads as a record of what was left off,
   *  but the ways OUT are withdrawn — leaving the page unmounts the progress
   *  indicator with it, and the run becomes invisible. */
  inFlight: boolean;
}) {
  const remaining = total - sample.length;

  return (
    <div className="space-y-3 rounded-md border border-amber-500/50 bg-amber-500/5 p-3">
      <div className="flex items-start gap-2">
        <AlertTriangle aria-hidden className="mt-0.5 size-4 shrink-0 text-amber-600" />
        <p className="font-medium text-sm">
          {total} {total === 1 ? 'expense has' : 'expenses have'} no amount for{' '}
          {monthLabel(invoiceMonth)} — those lines will be left off entirely.
        </p>
      </div>
      <p className="text-muted-foreground text-xs">
        Each draft they affect is flagged for review, so nothing is billed wrongly — but the invoice
        will be short until an amount is recorded.
      </p>

      <ul className="divide-y rounded-md border bg-background">
        {sample.map((line) => (
          <li className="px-3 py-2" key={`${line.customerName}_${line.description}`}>
            <p className="truncate text-sm">
              <span className="font-medium">{line.customerName}</span>
              <span className="ml-1.5 text-muted-foreground">{line.description}</span>
            </p>
            <p className="truncate text-muted-foreground text-xs">{line.itemName}</p>
          </li>
        ))}
      </ul>
      {remaining > 0 ? <p className="text-muted-foreground text-xs">and {remaining} more</p> : null}

      {/* Both routes to a fix. Each LEAVES the dialog, which is the honest
          thing: entering a month of amounts is not a sub-task of pressing
          Generate, and a nested import report inside the dialog would be a
          worse version of the page that already does it. The month travels in
          the link so the grid opens on the period being invoiced. */}
      {/* No onClick closing the sheet: navigating to another route unmounts it
          anyway, and dispatching a second navigation from the same click left
          the entry Back returns to decided by whichever one the router
          committed first — the open sheet, or one stripped of its month. */}
      {inFlight ? null : (
        <div className="flex flex-wrap gap-2">
          <Button asChild size="sm" variant="outline">
            <Link search={gridSearch} to="/invoices/variable-expenses">
              <Pencil className="size-4" />
              See which need values
            </Link>
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link search={{ ...gridSearch, import: true }} to="/invoices/variable-expenses">
              <Upload className="size-4" />
              Import a sheet
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
}
